// AutoEval.jsx — logique 100% intacte, layout carte par compétence
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Send, Info, Star, MessageSquare, CheckCircle, Download, ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './AutoEval.css';

import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchJobSkillsRequest } from '../../../redux/actions/jobSkillActions';
import { fetchNiveauSkillsRequest } from '../../../redux/actions/niveauSkillActions';
import { createSkillEvalRequest, updateSkillEvalRequest, fetchSkillEvalsByUserRequest } from '../../../redux/actions/skillEvaluationActions';

const GROUP_META = {
    METIER:      { icon: '⚙️', cls: 'ae-icon-metier', nom: 'Compétences Métiers' },
    SOFT_SKILLS: { icon: '💬', cls: 'ae-icon-soft',   nom: 'Soft Skills'         },
    AUTRES:      { icon: '📋', cls: 'ae-icon-autres', nom: 'Autres Compétences'  },
};

const GROUP_ACTIVE = {
    METIER:      'ae-level-card--active',
    SOFT_SKILLS: 'ae-level-card--active-red',
    AUTRES:      'ae-level-card--active',
};

export default function AutoEval() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { user } = useSelector(state => state.auth || {});
    const { list: campagnesList, loading: loadingCamp } = useSelector(state => state.campagnes || { list: [] });
    const { niveauSkills }  = useSelector(state => state.niveauSkill    || { niveauSkills: [] });
    const { jobSkills }     = useSelector(state => state.jobSkill       || { jobSkills: [] });
    const { skillEvaluations, loading: evalLoading } = useSelector(state => state.skillEvaluation || { skillEvaluations: [] });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [evaluations, setEvaluations]               = useState({});
    const [commentaireGlobal, setCommentaireGlobal]   = useState("");
    const [existingEval, setExistingEval]             = useState(null);
    const [showScrollTop, setShowScrollTop]           = useState(false);

    // ── Init data
    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchNiveauSkillsRequest());
        const currentUserId = user?.id || user?.personnelId || user?.uid;
        const roleId = user?.jobId || user?.posteId || user?.role?.id || user?.job?.id;
        if (roleId)        dispatch(fetchJobSkillsRequest(roleId));
        if (currentUserId) dispatch(fetchSkillEvalsByUserRequest(currentUserId));
    }, [dispatch, user]);

    // ── Scroll to top button
    useEffect(() => {
        const handleScroll = () => setShowScrollTop(window.scrollY > 300);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleDownloadDictionnaire = useCallback(() => {
        const link = document.createElement('a');
        link.href = '/assets/dictionnaire-competences.pdf';
        link.download = 'Dictionnaire_Competences.pdf';
        link.click();
    }, []);

    // ── Campagnes actives
    const activeCampagnes = useMemo(() =>
        (campagnesList || []).filter(c => c.status === true), [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const d = activeCampagnes.find(c => c.isDefault) || activeCampagnes[0];
            if (d) setSelectedCampagneId(d.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    // ── Chargement éval existante
    useEffect(() => {
        if (selectedCampagneId && skillEvaluations.length > 0) {
            const found = skillEvaluations.find(
                e => e.campagneId === parseInt(selectedCampagneId) && !e.evaluateurId
            );
            if (found) {
                setExistingEval(found);
                setCommentaireGlobal(found.commentaireGlobal || "");
                const evals = {};
                found.niveaux?.forEach(n => { evals[n.skillId] = n.scoreObtenu; });
                setEvaluations(evals);
            } else {
                setExistingEval(null);
                setEvaluations({});
                setCommentaireGlobal("");
            }
        } else {
            setExistingEval(null);
        }
    }, [selectedCampagneId, skillEvaluations]);

    const isReadOnly = useMemo(() =>
        existingEval && existingEval.statut !== "EN_ATTENTE_COLLAB", [existingEval]);

    // ── Axes groupés
    const formattedAxes = useMemo(() => {
        if (!jobSkills || jobSkills.length === 0) return [];
        const autoEvalSkills = jobSkills.filter(js =>
            !js.modeEvaluationNom ||
            js.modeEvaluationNom === "Auto-évaluation" ||
            js.modeEvaluationCode === "AUTO_EVAL"
        );
        const grouped = autoEvalSkills.reduce((acc, js) => {
            const key = js.skillType || "AUTRES";
            if (!acc[key]) {
                const meta = GROUP_META[key] || GROUP_META.AUTRES;
                acc[key] = { id: key, nom: meta.nom, competences: [] };
            }
            if (!acc[key].competences.some(c => c.id === js.skillId)) {
                acc[key].competences.push({
                    id: js.skillId,
                    nom: js.skillNom,
                    description: js.skillDescription || ""
                });
            }
            return acc;
        }, {});
        return Object.values(grouped);
    }, [jobSkills]);

    const sortedNiveaux = useMemo(() =>
            [...(niveauSkills || [])].sort((a, b) => (a.niveau || 0) - (b.niveau || 0)),
        [niveauSkills]
    );

    const handleEval = (compId, levelId) => {
        if (isReadOnly) return;
        setEvaluations(p => ({ ...p, [compId]: levelId }));
    };

    const totalCompetences = formattedAxes.reduce((acc, a) => acc + a.competences.length, 0);
    const evaluees  = Object.keys(evaluations).length;
    const progress  = totalCompetences === 0 ? 100 : Math.round((evaluees / totalCompetences) * 100);
    const isReady   = progress === 100;

    const handleSubmit = () => {
        const niveauxArray = Object.entries(evaluations).map(([skillId, score]) => ({
            skillId:     parseInt(skillId),
            scoreCollab: parseInt(score),
            scoreObtenu: parseInt(score),
            commentaire: ""
        }));
        const payload = {
            campagneId:       parseInt(selectedCampagneId),
            userId:           user.id || user.uid || user.personnelId,
            statut:           "EN_ATTENTE_MANAGER",
            dateEvaluation:   new Date().toISOString(),
            commentaireGlobal,
            niveaux:          niveauxArray
        };
        if (existingEval) dispatch(updateSkillEvalRequest(existingEval.id, payload));
        else              dispatch(createSkillEvalRequest(payload));
    };

    return (
        <div className="ae-root">

            {/* ══ HEADER ══ */}
            <div className="ae-header">

                {/* Ligne haute */}
                <div className="ae-header__top">
                    <div className="ae-header__brand">
                        <div className="ae-header__icon">
                            <Star size={16} color="#fff" />
                        </div>
                        <div>
                            <h1 className="ae-header__title">Auto-évaluation des compétences</h1>
                            <p className="ae-header__sub">Évaluez vos compétences pour la campagne de performance</p>
                        </div>
                    </div>

                    <div className="ae-header__actions">
                        <button className="ae-btn-dict" onClick={handleDownloadDictionnaire}>
                            <Download size={12} />
                            Télécharger le dictionnaire de compétences
                        </button>

                        <div className="ae-camp-zone">
                            <span className="ae-camp-label">Campagne</span>
                            {loadingCamp ? (
                                <span className="ae-hint">Chargement…</span>
                            ) : (
                                <select className="ae-select" value={selectedCampagneId}
                                        onChange={e => setSelectedCampagneId(e.target.value)}>
                                    <option value="">— Sélectionnez —</option>
                                    {activeCampagnes.map(c =>
                                        <option key={c.id} value={c.id}>{c.nom}</option>)}
                                </select>
                            )}
                        </div>
                    </div>
                </div>

                {/* Ligne basse : progression + soumettre */}
                {selectedCampagneId && (
                    <div className="ae-progress-bar-zone">
                        <div className="ae-progress-track-wrap">
                            <div className="ae-progress-info">
                                <span className="ae-progress-label">
                                    {evaluees} / {totalCompetences} compétences évaluées
                                </span>
                                <span className="ae-progress-pct">{progress}%</span>
                            </div>
                            <div className="ae-track">
                                <div className={`ae-fill${isReady ? ' ae-fill--complete' : ''}`}
                                     style={{ width: `${progress}%` }} />
                            </div>
                        </div>

                        {!isReadOnly && (
                            <div className="ae-progress-submit">
                                <button
                                    disabled={!isReady || evalLoading}
                                    onClick={handleSubmit}
                                    className={`ae-btn${isReady && !evalLoading ? ' ae-btn--primary' : ' ae-btn--disabled'}`}>
                                    <Send size={12} />
                                    {evalLoading ? "Envoi…" : "Soumettre l'évaluation"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ══ CONTENU ══ */}
            {!selectedCampagneId ? (
                <div className="ae-empty">
                    <div className="ae-empty-icon"><Star size={22} color="#204385" /></div>
                    <h3>Aucune campagne sélectionnée</h3>
                    <p>Sélectionnez une campagne active pour commencer votre évaluation.</p>
                </div>
            ) : (
                <div className="ae-body">

                    {isReadOnly && (
                        <div className="ae-banner">
                            <Info size={14} />
                            <span>Évaluation soumise — statut : <strong>{existingEval.statut}</strong></span>
                        </div>
                    )}

                    {/* ══ SECTIONS GROUPES ══ */}
                    {formattedAxes.map(axe => {
                        const meta        = GROUP_META[axe.id] || GROUP_META.AUTRES;
                        const activeClass = GROUP_ACTIVE[axe.id] || 'ae-level-card--active';

                        return (
                            <div key={axe.id} className="ae-section">

                                {/* Titre du groupe */}
                                <div className="ae-section__header">
                                    <div className={`ae-section__icon ${meta.cls}`}>{meta.icon}</div>
                                    <span className="ae-section__title">{axe.nom}</span>
                                    <span className="ae-section__badge">{axe.competences.length} élément{axe.competences.length > 1 ? 's' : ''}</span>
                                </div>

                                {/* Une carte par compétence */}
                                {axe.competences.map(comp => {
                                    const isEvaluated   = evaluations[comp.id] !== undefined;
                                    const existingLevel = existingEval?.niveaux?.find(n => n.skillId === comp.id);

                                    return (
                                        <div key={comp.id}
                                             className={`ae-comp-card${isEvaluated ? ' ae-comp-card--done' : ''}`}>

                                            {/* Nom + description + icône */}
                                            <div className="ae-comp-card__top">
                                                <div className="ae-comp-card__info">
                                                    <div className="ae-comp-card__name">
                                                        {comp.nom}
                                                        {isEvaluated && !isReadOnly && (
                                                            <span className="ae-skill-check">
                                                                <CheckCircle size={10} color="#fff" />
                                                            </span>
                                                        )}
                                                    </div>
                                                    {comp.description && (
                                                        <div className="ae-comp-card__desc">{comp.description}</div>
                                                    )}
                                                </div>
                                                <div className={`ae-comp-card__type-icon ${meta.cls}`}>
                                                    {meta.icon}
                                                </div>
                                            </div>

                                            {/* Niveaux ou scores lecture */}
                                            {isReadOnly ? (
                                                <div className="ae-score-pair">
                                                    <div className="ae-score-item">
                                                        <span className="ae-score-label">Votre note</span>
                                                        <span className="ae-score-val">{existingLevel?.scoreObtenu ?? "—"}</span>
                                                    </div>
                                                    <div className="ae-score-item">
                                                        <span className="ae-score-label">Manager</span>
                                                        <span className="ae-score-val ae-score-val--mgr">{existingLevel?.scoreManager ?? "—"}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="ae-levels">
                                                    {sortedNiveaux.map(niv => {
                                                        const isActive = evaluations[comp.id] === niv.id;
                                                        return (
                                                            <div key={niv.id}
                                                                 className={`ae-level-card${isActive ? ` ${activeClass}` : ''}`}
                                                                 onClick={() => handleEval(comp.id, niv.id)}
                                                                 title={niv.description || niv.nom}>
                                                                <span className="ae-level-num">{niv.niveau}</span>
                                                                <span className="ae-level-nom">{niv.nom}</span>
                                                                {niv.description && (
                                                                    <span className="ae-level-desc">{niv.description}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}

                    {/* ══ COMMENTAIRE ══ */}
                    <div className="ae-comment-card">
                        <div className="ae-comment-card__title">
                            <MessageSquare size={13} color="#9ca3af" />
                            Commentaires globaux
                        </div>
                        <textarea
                            className="ae-textarea"
                            placeholder="Justifiez vos choix ou exprimez vos besoins de formation pour l'année prochaine…"
                            value={commentaireGlobal}
                            onChange={e => setCommentaireGlobal(e.target.value)}
                            rows={4}
                            disabled={isReadOnly}
                        />
                    </div>

                    {/* Note confirmation si tout évalué */}
                    {isReady && !isReadOnly && (
                        <div className="ae-footer-note">
                            <CheckCircle size={14} />
                            Toutes les compétences ont été évaluées — soumettez via le bouton en haut.
                        </div>
                    )}

                </div>
            )}

            {/* ══ SCROLL TO TOP ══ */}
            <button
                className={`ae-scroll-top${showScrollTop ? ' ae-scroll-top--visible' : ''}`}
                onClick={scrollToTop}
                aria-label="Revenir en haut de la page"
                title="Revenir en haut">
                <ArrowUp size={17} />
            </button>

        </div>
    );
}